import chroma from 'chroma-js'

// Utility function to convert hex to RGB
export const hexToRgb = (hex: string) => {
  // Remove the hash at the start if it's there
  hex = hex.replace(/^#/, '')

  // Parse the r, g, b values
  const bigint = parseInt(hex, 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255

  return `${r}, ${g}, ${b}`
}

export type DossColor = {
  1: string
  2: string
  3: string
  4: string
  5: string
  6: string
  7: string
  8: string
  9: string
  10: string
  11: string
  12: string
}

export const blue: DossColor = {
  '1': '#F5F8FE',
  '2': '#ECF0FE',
  '3': '#E2E8FD',
  '4': '#D9E0FD',
  '5': '#CFD8FC',
  '6': '#B1C0FB',
  '7': '#7791F8',
  '8': '#3D62F5',
  '9': '#0B36E5',
  '10': '#092AAE',
  '11': '#061C74',
  '12': '#041044',
}

export const purple: DossColor = {
  '1': '#FBFAFF',
  '2': '#EDE6FD',
  '3': '#DED1FC',
  '4': '#D7C6FB',
  '5': '#C2A8F9',
  '6': '#A580F5',
  '7': '#8957F2',
  '8': '#6C2EEF',
  '9': '#5B26CB',
  '10': '#4A1EA8',
  '11': '#391584',
  '12': '#311172',
}

export const gray: DossColor = {
  '1': '#FCFCFC',
  '2': '#F7F7F7',
  '3': '#F0F0F0',
  '4': '#E8E8E8',
  '5': '#E1E1E1',
  '6': '#D9D9D9',
  '7': '#CECECE',
  '8': '#999999',
  '9': '#6d6d6d',
  '10': '#333333',
  '11': '#171717',
  '12': '#000000',
}

export const white = '#FFFFFF'

export const brightBlue = '#1439ff'

export const black = '#000000'

export const greenSuccess = '#30A46C'

export const yellow = {
  '1': '#FFFCF2',
  '2': '#FFFBEA',
  '3': '#FFFAE2',
  '4': '#FEF6D5',
  '5': '#FFF2C4',
  '6': '#FBEAAB',
  '7': '#FAE38D',
  '8': '#ECCC54',
  '9': '#E8BC18',
  '10': '#C9A317',
  '11': '#987B0E',
  '12': '#785F0A',
}

export const amber = {
  '1': '#fefdfb',
  '2': '#fefbe9',
  '3': '#fff7c2',
  '4': '#ffee9c',
  '5': '#fbe577',
  '6': '#f3d673',
  '7': '#e9c162',
  '8': '#e2a336',
  '9': '#ffc53d',
  '10': '#ffba18',
  '11': '#ab6400',
  '12': '#4f3422',
}

export const overlays: DossColor = {
  '1': chroma(black).alpha(0.01).css(),
  '2': chroma(black).alpha(0.03).css(),
  '3': chroma(black).alpha(0.05).css(),
  '4': chroma(black).alpha(0.06).css(),
  '5': chroma(black).alpha(0.08).css(),
  '6': chroma(black).alpha(0.12).css(),
  '7': chroma(black).alpha(0.16).css(),
  '8': chroma(black).alpha(0.24).css(),
  '9': chroma(black).alpha(0.4).css(),
  '10': chroma(black).alpha(0.48).css(),
  '11': chroma(black).alpha(0.56).css(),
  '12': black,
}

export const green: DossColor = {
  '1': '#F8FCFA',
  '2': '#F1F9F6',
  '3': '#EDF8F4',
  '4': '#D9F2E8',
  '5': '#C3EADB',
  '6': '#ACE2CD',
  '7': '#86D5B8',
  '8': '#42BE65',
  '9': '#109363',
  '10': '#0E8157',
  '11': '#0C6E4A',
  '12': '#052E1F',
}

export const red: DossColor = {
  '1': '#FEFAFB',
  '2': '#FEF6F6',
  '3': '#FFEEF0',
  '4': '#FED8DA',
  '5': '#FDC9CC',
  '6': '#FCBABF',
  '7': '#F99AA0',
  '8': '#FA4D56',
  '9': '#EF2734',
  '10': '#DF111E',
  '11': '#B90611',
  '12': '#72080F',
}

export const orange: DossColor = {
  '1': '#FFFCFA',
  '2': '#FEF9F6',
  '3': '#FFF3EB',
  '4': '#FFE8D7',
  '5': '#FFDCC3',
  '6': '#FFCCA7',
  '7': '#FFB585',
  '8': '#F77A26',
  '9': '#DA5B06',
  '10': '#C75305',
  '11': '#B34B05',
  '12': '#4F2102',
}

export const primary = blue

export const optional = purple

export const info = blue

export const success = green

export const warning = orange

export const error = red

export const content = {
  primary: gray[10],
  secondary: gray[10],
  white,
  brightBlue,
  disabled: gray[9],
  danger: red[9],
  warning: yellow[9],
} as const

const border = {
  normal: gray[4],
  hover: gray[5],
  focus: blue[4],
  warning: yellow[4],
  danger: red[5],
} as const

const background = {
  white,
  offWhite: gray[2],
  disabled: gray[1],
} as const

export const uiColors = {
  content,
  background,
  border,
} as const

export type UIColors = typeof uiColors

export const palette = {
  blue,
  purple,
  gray,
  white,
  brightBlue,
  black,
  yellow,
  amber,
  overlays,
  green,
  red,
  orange,
}

export const AVATAR_COLORS = [
  'bg-red-3 text-red-9 border-red-9',
  'bg-orange-3 text-orange-9 border-orange-9',
  'bg-yellow-3 text-yellow-10 border-yellow-10',
  'bg-green-3 text-green-9 border-green-9',
  'bg-blue-3 text-blue-9 border-blue-9',
  'bg-purple-2 text-purple-9 border-purple-9',
]

/**
 * Get a randomly deterministic color class for an avatar based on a string.
 * @param str The string to get the avatar color for.
 * @returns The color class for the avatar.
 */
export function getAvatarColorsForString(str: string) {
  // djb2 hashing algorithm
  // to find a randomly distributed integer from a string
  // https://en.wikipedia.org/wiki/Universal_hashing#Hashing_strings
  let hash = 5381 // djb's prime odd deficient number used as a seed
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    // the djb2 algorithm is hash * 33 + char
    // instead of using * 33, which is multiplication, we can use a bit shift and addition
    // 5-bit shift plus self is equivalent to * 33 while being more computationally efficient
    hash = (hash << 5) + hash + char
    hash = hash & hash // Convert to 32-bit integer
  }
  // Use the absolute value of the hash to get a positive index
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}
